import React from 'react';

/**
 * Formats text with WhatsApp-style bolding (*text*) into JSX with <strong> tags.
 * @param {string} text - The text to format.
 * @returns {JSX.Element|string} - The formatted JSX or original string.
 */
export const renderFormattedText = (text) => {
  if (!text) return null;
  
  // Replace *text* with <strong>text</strong>
  const parts = text.split(/(\*.*?\*)/g);
  
  if (parts.length === 1) return text;

  return parts.map((part, index) => {
    if (part.startsWith('*') && part.endsWith('*')) {
      return <strong key={index}>{part.slice(1, -1)}</strong>;
    }
    return part;
  });
};
 
/**
 * Maps WhatsApp Cloud API error codes into friendly, actionable descriptions
 * @param {object|string} error - Error object or string
 * @returns {string} - Human-readable error explanation
 */
export const getWhatsAppErrorMessage = (error) => {
  if (!error) return 'Delivery failed on WhatsApp';
  
  const code = Number(error.code || error.error_code);
  const details = error.details || error.message || error.title || '';
  const explanation = error.explanation || '';

  const knownCodes = {
    131030: 'App is in Development Mode: recipient phone number is not in Meta’s allowed test numbers list. Switch App to Live mode in developers.facebook.com or add number as test recipient.',
    131042: 'Payment / Billing Issue: Meta WhatsApp Business Account has no payment method or an unpaid balance.',
    131053: 'Media Download / Upload Error: Meta could not download the PDF from your URL. Ensure the URL is a direct, public HTTPS file link (not a Google Drive preview page).',
    131052: 'Media Download Error: Meta was unable to download the media file.',
    132000: 'Template Parameter Mismatch: Number of variables passed does not match template placeholders {{1}}, {{2}}.',
    132001: 'Template Not Found: The template name does not exist or language code (en vs en_US) does not match approved template in Meta.',
    132005: 'Template Hydrated Text Too Long: Message length exceeded 1024 characters after filling variables.',
    132007: 'Template Paused: Template paused by Meta due to user blocks or low quality rating.',
    132015: 'Template Paused: Template is currently paused in WhatsApp Manager.',
    132016: 'Template Disabled: Template was disabled by Meta.',
    131026: 'Message Undeliverable: Recipient phone number is not on WhatsApp, has blocked business, or is unreachable.',
    131047: '24-Hour Rule: More than 24 hours have passed since the guest last messaged. You must use an approved template.',
    131051: 'Unsupported Message Type: The format is not supported by WhatsApp Cloud API.',
    130429: 'Rate Limit Exceeded: Message throughput limit reached on WhatsApp Business Account.',
    130472: 'Marketing Restriction (Meta Experiment): Recipient is in Meta’s anti-marketing experiment. Solution: Re-create this template as a "UTILITY" template instead of "MARKETING" in WhatsApp Manager.',
    131049: 'Marketing Frequency Cap: Recipient reached Meta’s daily limit for marketing messages. Solution: Re-create this template as a "UTILITY" template instead of "MARKETING" in WhatsApp Manager.'
  };

  const mainReason = knownCodes[code] || explanation || error.title || error.message || 'Delivery Failure';
  return `Error ${code || 'Meta'}: ${mainReason}${details && !mainReason.includes(details) ? ` (${details})` : ''}`;
};
